/* ==========================================================================
   <variant-picker>
   Wraps form, syncs selected variant on radio change.
   Updates URL, price, ATC button state.
   Dispatches `variant:change` event for media-gallery to listen.

   Also drives three conversion mechanics (all degrade gracefully if their
   markup / data is absent):
     1. Low-stock count  — truthful per-variant "only X left"
     2. Mobile sticky ATC — mirrors the main ATC, shown when it scrolls away
     3. Back-in-stock     — Klaviyo subscribe form on sold-out variants
   ========================================================================== */

class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.product = this._readProduct();
    if (!this.product) return;

    this.form = this.querySelector('form');
    this.priceEl = this.querySelector('[data-variant-price]');
    this.atcBtn  = this.querySelector('[data-atc]');
    this.idInput = this.querySelector('input[name="id"]');
    this.payment = this.form?.querySelector('[data-native-payment]');
    if (this.payment) {
      this._onPaymentDiscountReady = () => this._syncPaymentAvailability();
      document.addEventListener('ev:payment-discount-ready', this._onPaymentDiscountReady);
    }
    this.sizeGroup = this.querySelector('[data-size-option]');
    this.strChooseSize = this.dataset.strChooseSize || '';

    // --- 1. low-stock count ---------------------------------------------
    this.inventory = this._readInventory();
    this.threshold = parseInt(this.dataset.lowStockThreshold || '3', 10);
    this.strLast = this.dataset.strStockLast || '';
    this.strFew = this.dataset.strStockFew || '';
    this.strInStock = this.dataset.strInStock || '';
    try { this.sourcedOrders = JSON.parse(this.querySelector('[data-sourced-order-json]')?.textContent || '{}'); }
    catch (_) { this.sourcedOrders = {}; }
    this.strSourcedOrder = this.dataset.strSourcedOrder || '';
    this.strOrder = this.dataset.strOrder || '';
    this.strAddToBag = this.dataset.strAddToBag || this.atcBtn?.dataset.label || '';
    this.strSoldOut = this.dataset.strSoldOut || '';
    // Coming-soon products (badge:coming-soon) are unavailable but must NOT read
    // as "Sold out" — the server renders "Coming soon", so update() must keep it.
    this.strComingSoon = this.dataset.strComingSoon || '';
    this.comingSoon = this.dataset.comingSoon === 'true';
    this.stockCountEl = this.querySelector('[data-stock-count]');
    this.stockLabelEl = this.querySelector('[data-stock-label]');
    this.stockDotEl = this.querySelector('[data-stock-dot]');

    // --- 2. mobile sticky ATC -------------------------------------------
    this.sticky = this.querySelector('[data-sticky-atc]');
    this.stickyBtn = this.querySelector('[data-sticky-atc-btn]');
    this.stickyPrice = this.querySelector('[data-sticky-price]');
    this._setupSticky();

    // --- 3. back-in-stock ------------------------------------------------
    this.bis = this.querySelector('[data-bis]');
    this.bisForm = this.querySelector('[data-bis-form]');
    this.bisEmail = this.querySelector('[data-bis-email]');
    this.bisVariant = this.querySelector('[data-bis-variant]');
    this.bisSubmit = this.querySelector('.pdp__bis-submit');
    this.bisMsg = this.querySelector('[data-bis-msg]');
    if (this.bisForm) this.bisForm.addEventListener('submit', (e) => this._submitBIS(e));

    this._setupSizePrompt();
    this._setupSourceOrder();
    this._onVariantChange = this._onVariantChange || ((e) => {
      if (e.target?.matches('input[type="radio"]')) {
        if (e.target.closest('[data-size-option]')) delete this.dataset.carriedSize;
        this._userInteracted = true;
        this.update();
      }
    });
    this.addEventListener('change', this._onVariantChange);

    this.update();
    // A color swap replaces the entire product form. Rebind its native wallet
    // only after the carried size has updated the form's variant ID.
    if (this.payment) {
      try { window.Shopify?.PaymentButton?.init?.(); }
      catch (_) { /* Payment SDK failures must not interrupt ordinary shopping. */ }
    }
  }

  disconnectedCallback() {
    this._sourceController?.abort();
    this.querySelector('sourced-order-info')?.cancelPurchase?.();
    this._stickyObserver?.disconnect();
    if (this._onPaymentDiscountReady) document.removeEventListener('ev:payment-discount-ready', this._onPaymentDiscountReady);
  }

  _readProduct() {
    const json = this.querySelector('[data-product-json]')?.textContent;
    try { return JSON.parse(json); } catch (e) { return null; }
  }

  _readInventory() {
    const json = this.querySelector('[data-inventory-json]')?.textContent;
    try { return JSON.parse(json); } catch (e) { return null; }
  }

  selectedOptions() {
    // Read the checked value per option IN PRODUCT-OPTION ORDER, not DOM order.
    // Color swatches render before size pills in the markup, so DOM order does
    // not match variant.options order — a positional compare needs product order.
    const names = (this.product.options || []).map((o) => (typeof o === 'string' ? o : o.name));
    return names.map((name) => {
      const el = this.querySelector(`input[type="radio"][name="options[${name}]"]:checked`);
      return el ? el.value : null;
    });
  }

  findVariant() {
    const sel = this.selectedOptions();
    return this.product.variants.find((v) =>
      v.options.length === sel.length &&
      v.options.every((opt, i) => opt === sel[i])
    );
  }

  _supplierIntent(variant) {
    const quantity = this.inventory?.[variant?.id];
    return this.dataset.supplierProduct === 'true' && typeof quantity === 'number' && quantity <= 0;
  }

  _canPurchase(variant) {
    return Boolean(variant?.available && (!this._supplierIntent(variant) || this.sourcedOrders?.[variant.id] === true));
  }

  update() {
    this.querySelector('sourced-order-info')?.cancelPurchase?.();
    this._updateCarriedSizeNotice();
    if (this._sizePending()) {
      this._setVariantInput(null);
      if (this.atcBtn) this.atcBtn.type = 'button';
      for (const button of [this.atcBtn, this.stickyBtn]) {
        if (!button) continue;
        button.disabled = false;
        this._setButtonLabel(button, this.dataset.supplierProduct === 'true' ? this.strOrder : this.strChooseSize);
      }
      if (this.dataset.supplierProduct === 'true') {
        this.querySelectorAll('[data-sourced-order-notice]').forEach(el => { el.hidden = false; });
        this.querySelectorAll('[data-source-order-pending]').forEach(el => { el.hidden = true; });
        this.querySelectorAll('[data-source-size-prompt]').forEach(el => { el.hidden = false; });
      }
      if (this.stockCountEl) this.stockCountEl.textContent = '';
      const delivery = this.querySelector('.pdp__delivery-estimate');
      if (delivery) delivery.hidden = !this.product.variants.some(v => v.available) || this.dataset.supplierProduct === 'true';
      if (this.bis) this.bis.hidden = this.dataset.supplierProduct === 'true' || this.product.variants.some(variant => variant.available);
      if (this.bisVariant) this.bisVariant.value = '';
      return;
    }
    const variant = this.findVariant();
    if (!variant) {
      this._setVariantInput(null);
      if (this.atcBtn) {
        this.atcBtn.disabled = true;
        this._setButtonLabel(
          this.atcBtn,
          (this.comingSoon ? this.strComingSoon : this.strSoldOut) || this.atcBtn.textContent
        );
      }
      if (this.stickyBtn) { this.stickyBtn.disabled = true; this._setButtonLabel(this.stickyBtn, this.atcBtn?.textContent || this.strSoldOut); }
      return;
    }

    if (this.sizeGroup) {
      if (this.atcBtn) this.atcBtn.type = 'submit';
      this.sizeGroup.removeAttribute('aria-invalid');
      const prompt = this.sizeGroup.querySelector('[data-size-selection-prompt]');
      if (prompt) prompt.hidden = true;
    }

    this._setVariantInput(variant);
    const stockState = this.querySelector('.pdp__stock-state');
    if (stockState) stockState.hidden = false;

    if (this.priceEl) {
      this.priceEl.innerHTML = this._renderPrice(variant);
    }

    if (this.atcBtn) {
      this.atcBtn.disabled = !this._canPurchase(variant);
      this._setButtonLabel(this.atcBtn, this._supplierIntent(variant) ? this.strOrder : variant.available
        ? (this.strAddToBag || 'Add to bag')
        : (this.comingSoon ? (this.strComingSoon || this.strSoldOut) : (this.strSoldOut || 'Sold out')));
    }

    this._updateOptionLabels();
    this._updateStock(variant);
    this._mirrorSticky(variant);
    this._updateBIS(variant);

    if (window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState(window.history.state, '', url.toString());
      document.querySelectorAll?.('form[action$="/localization"] input[name="return_to"]')?.forEach(input => {
        input.value = `${url.pathname}${url.search}`;
      });
    }

    this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
  }

  _setVariantInput(variant) {
    const available = this._canPurchase(variant);
    // Source purchases disclose their delivery before adding; wallets remain
    // available through the ordinary cart/checkout, after that confirmation.
    this._paymentVariantAvailable = available && !this._supplierIntent(variant);
    this._syncPaymentAvailability();
    if (!this.idInput) return;
    this.idInput.value = variant?.id ?? '';
    this.idInput.disabled = !available;
    // Shopify's product payment component observes the native ID input, not
    // our custom variant:change event (the same contract used by Shopify Dawn).
    if (this.payment) this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  _syncPaymentAvailability() {
    if (!this.payment) return;
    const available = this._paymentVariantAvailable && window.EssentialsVault?.cartDiscount?.paymentReady === true;
    this.payment.hidden = !available;
    this.payment.toggleAttribute('inert', !available);
  }

  _updateCarriedSizeNotice() {
    const notice = this.querySelector('[data-color-size-notice]');
    if (!notice) return;
    const requestedSize = this.dataset.carriedSize;
    const unavailable = Boolean(requestedSize && !this.findVariant()?.available);
    notice.hidden = !unavailable;
    notice.textContent = unavailable
      ? (this.dataset.strSizeUnavailable || '').replace('__SIZE__', requestedSize)
      : '';
  }

  _sizePending() {
    return Boolean(this.sizeGroup && !this.sizeGroup.querySelector('input:checked'));
  }

  _setupSourceOrder() {
    if (this.dataset.supplierProduct !== 'true') return;
    this._sourceController?.abort();
    this._sourceController = new AbortController();
    const { signal } = this._sourceController;
    const intercept = event => {
      if (event.type === 'click' && event.target.closest('[data-atc]') !== this.atcBtn) return;
      if (event.type === 'submit' && event.target !== this.form) return;
      const variant = this.findVariant();
      if (!this._supplierIntent(variant) && !this._sizePending()) return;
      if (event.type === 'submit' && this._sourceApproval === String(variant?.id) && this._canPurchase(variant)) {
        this._sourceApproval = null;
        return;
      }
      // Capture clicks as well: the published cart drawer handles product
      // button clicks directly, without first emitting a native submit event.
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this._sizePending()) { this._promptForSize(); return; }
      if (!this._canPurchase(variant) || this.atcBtn?.getAttribute('aria-busy') === 'true') return;
      if (!this.form?.reportValidity()) return;
      const variantId = String(variant.id);
      const opener = this._sourceOpener || this.atcBtn;
      this.querySelector('sourced-order-info')?.openInfo?.(opener, `${this.product.title} · ${variant.title}`, () => {
        if (!this.isConnected || this._sizePending() || String(this.findVariant()?.id) !== variantId || !this._canPurchase(this.findVariant()) || this.atcBtn.disabled) return;
        this._sourceApproval = variantId;
        try { this.form.requestSubmit(this.atcBtn); }
        finally { this._sourceApproval = null; }
      });
    };
    this.addEventListener('click', intercept, { capture: true, signal });
    this.addEventListener('submit', intercept, { capture: true, signal });
  }

  _setupSizePrompt() {
    if (!this.sizeGroup || this._sizePromptReady) return;
    this._sizePromptReady = true;
    this.atcBtn?.addEventListener('click', (event) => {
      if (!this._sizePending()) return;
      event.preventDefault();
      this._promptForSize();
    });
    // Capture before the cart drawer's form-level listener, in either script
    // load order. Size/color labels alone must never submit a default variant.
    this.addEventListener('submit', (event) => {
      if (event.target !== this.form) return;
      if (this._sizePending() || !this.findVariant()?.available) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this._sizePending()) this._promptForSize();
      }
    }, true);
  }

  _promptForSize() {
    if (!this.sizeGroup) return;
    this.sizeGroup.setAttribute('aria-invalid', 'true');
    const prompt = this.sizeGroup.querySelector('[data-size-selection-prompt]');
    if (prompt) prompt.hidden = false;
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    this.sizeGroup.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
    // Focus the group, not the first radio: prompting must not select XS.
    this.sizeGroup.focus({ preventScroll: true });
  }

  _renderPrice(variant) {
    const currency = window.Shopify?.currency?.active || window.theme?.currency || 'CZK';
    // Zero-decimal for currencies displayed without minor units (CZK/HUF here);
    // everything else keeps 2 so e.g. EUR 79.90 isn't rounded to 80.
    const zeroDp = ['CZK', 'HUF', 'JPY', 'KRW', 'ISK', 'CLP'];
    const format = cents => {
      if (typeof window.theme?.formatMoney === 'function') return window.theme.formatMoney(cents);
      const fd = currency === 'CZK' && Number(cents) % 100 !== 0 ? 2 : (zeroDp.includes(currency) ? 0 : 2);
      return new Intl.NumberFormat(window.theme?.locale || 'cs-CZ', {
        style: 'currency', currency, minimumFractionDigits: fd, maximumFractionDigits: fd,
      }).format(cents / 100);
    };
    const cur = format(variant.price);
    const cmp = variant.compare_at_price > variant.price ? format(variant.compare_at_price) : null;
    if (cmp) {
      return `<span class="price price--on-sale"><span class="price__compare text-mono">${cmp}</span><span class="price__current text-mono">${cur}</span></span>`;
    }
    return `<span class="price"><span class="price__current text-mono">${cur}</span></span>`;
  }

  /* Legends read "Size: XL" / "Color: Black". Server-rendered from the first
     variant, so they go stale the moment a shopper picks another one. */
  _updateOptionLabels() {
    this.querySelectorAll('[data-option-current]').forEach((el) => {
      const name = el.dataset.optionName;
      if (!name) return;
      const checked = this.querySelector(
        `input[type="radio"][name="options[${name}]"]:checked`
      );
      if (checked) el.textContent = checked.value;
    });
  }

  _setButtonLabel(button, text) {
    const label = button?.querySelector('[data-atc-label]');
    if (label) label.textContent = text;
    else if (button) button.textContent = text;
  }

  /* --- 1. low-stock count ---------------------------------------------- */
  _updateStock(variant) {
    const sourced = this.sourcedOrders?.[variant.id] === true;
    const intent = this._supplierIntent(variant);
    this.querySelectorAll('[data-source-size-prompt]').forEach(el => { el.hidden = true; });
    this.querySelectorAll('[data-source-order-pending]').forEach(el => { el.hidden = !intent || sourced; });
    this.querySelectorAll('[data-sourced-order-notice]').forEach(el => { el.hidden = !intent; });
    this.querySelectorAll('[data-physical-order]').forEach(el => { el.hidden = intent; });
    this.querySelectorAll('[data-sourced-order-property]').forEach(el => { el.disabled = !sourced; });
    if (intent) {
      if (this.stockCountEl) this.stockCountEl.textContent = '';
      if (this.stockLabelEl) { this.stockLabelEl.textContent = this.strSourcedOrder; this.stockLabelEl.hidden = false; }
      this.stockDotEl?.classList.remove('pdp__stock-dot--in', 'pdp__stock-dot--low');
      this.stockDotEl?.classList.add('pdp__stock-dot--out');
      const delivery = this.querySelector('.pdp__delivery-estimate');
      if (delivery) delivery.hidden = true;
      return;
    }
    if (this.hasAttribute('data-stock-compact')) {
      if (this.stockCountEl) this.stockCountEl.textContent = '';
      if (this.stockLabelEl) {
        this.stockLabelEl.textContent = variant.available ? this.strInStock : (this.comingSoon ? this.strComingSoon : this.strSoldOut);
        this.stockLabelEl.hidden = false;
      }
      this.stockDotEl?.classList.toggle('pdp__stock-dot--in', variant.available);
      this.stockDotEl?.classList.toggle('pdp__stock-dot--out', !variant.available);
      this.stockDotEl?.classList.remove('pdp__stock-dot--low');
      const delivery = this.querySelector('.pdp__delivery-estimate');
      if (delivery) delivery.hidden = !variant.available;
      return;
    }
    const qty = this.inventory ? this.inventory[variant.id] : null;
    let txt = '';
    if (variant.available && typeof qty === 'number') {
      if (qty === 1) txt = this.strLast;
      else if (qty > 1 && qty <= this.threshold) txt = this.strFew.replace('[n]', qty);
    }
    if (this.stockCountEl) this.stockCountEl.textContent = txt;

    // Keep the status label/dot in sync with the selected variant. When a
    // scarcity message shows, suppress the generic "in stock" label so the two
    // never read as one clumsy "in stock, last piece" string.
    if (this.stockLabelEl) {
      const outLabel = this.comingSoon ? (this.strComingSoon || this.strSoldOut) : this.strSoldOut;
      this.stockLabelEl.textContent = variant.available ? this.strInStock : outLabel;
      this.stockLabelEl.hidden = variant.available && txt !== '';
    }
    if (this.stockDotEl) {
      this.stockDotEl.classList.toggle('pdp__stock-dot--in', variant.available && txt === '');
      this.stockDotEl.classList.toggle('pdp__stock-dot--low', variant.available && txt !== '');
      this.stockDotEl.classList.toggle('pdp__stock-dot--out', !variant.available);
    }
  }

  /* --- 2. mobile sticky ATC -------------------------------------------- */
  _setupSticky() {
    if (!this.sticky || !this.atcBtn) return;
    if (this.stickyBtn) {
      this.stickyBtn.addEventListener('click', () => {
        if (!this.form) return;
        if (this._sizePending()) { this._promptForSize(); return; }
        if (!this._canPurchase(this.findVariant())) return;
        this._sourceOpener = this.stickyBtn;
        try { this.atcBtn?.click(); }
        finally { this._sourceOpener = null; }
      });
    }
    if (!('IntersectionObserver' in window)) return;
    const footer = globalThis.document?.querySelector?.('footer') || null;
    let mainButtonVisible = true;
    let footerVisible = false;
    this._stickyObserver?.disconnect();
    this._stickyObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === this.atcBtn) mainButtonVisible = entry.isIntersecting;
        if (entry.target === footer) footerVisible = entry.isIntersecting;
      }
      const show = !mainButtonVisible && !footerVisible;
      this.sticky.toggleAttribute('data-visible', show);
      this.sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
    }, { threshold: 0 });
    this._stickyObserver.observe(this.atcBtn);
    if (footer) this._stickyObserver.observe(footer);
  }

  _mirrorSticky(variant) {
    if (this.stickyBtn && this.atcBtn) {
      this.stickyBtn.disabled = this.atcBtn.disabled;
      const label = this.atcBtn.querySelector('[data-atc-label]');
      this._setButtonLabel(this.stickyBtn, label?.textContent || this.atcBtn.textContent);
    }
    if (this.stickyPrice) this.stickyPrice.innerHTML = this._renderPrice(variant);
  }

  /* --- 3. back-in-stock (Klaviyo client API) --------------------------- */
  _updateBIS(variant) {
    if (!this.bis) return;
    if (this._supplierIntent(variant)) { this.bis.hidden = true; return; }
    const wasHidden = this.bis.hidden;
    this.bis.hidden = variant.available;
    if (!variant.available && this.bisVariant) this.bisVariant.value = variant.id;
    // When the user actively picks a sold-out size, surface the notify form so
    // the dead-end "Sold out" reads as an action. Not on initial page load.
    if (!variant.available && wasHidden && this._userInteracted) {
      this.bis.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      if (this.bisEmail) this.bisEmail.focus({ preventScroll: true });
    }
  }

  async _submitBIS(e) {
    e.preventDefault();
    if (this._sizePending()) {
      this._promptForSize();
      return;
    }
    const selected = this.findVariant();
    if (!selected || selected.available) return;
    const key = this.bis?.dataset.klaviyoKey;
    const email = this.bisEmail?.value.trim();
    const variantId = this.bisVariant?.value;
    if (!key || !email || !variantId) return;
    if (this.bisSubmit) this.bisSubmit.disabled = true;
    try {
      const res = await fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(key)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              profile: { data: { type: 'profile', attributes: { email } } },
              channels: ['EMAIL']
            },
            relationships: {
              variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } }
            }
          }
        })
      });
      this._showBISMsg(res.ok);
    } catch (_) {
      this._showBISMsg(false);
    } finally {
      if (this.bisSubmit) this.bisSubmit.disabled = false;
    }
  }

  _showBISMsg(ok) {
    if (!this.bisMsg) return;
    this.bisMsg.hidden = false;
    this.bisMsg.textContent = ok ? (this.bisMsg.dataset.msgOk || '') : (this.bisMsg.dataset.msgErr || '');
    if (ok && this.bisForm) this.bisForm.hidden = true;
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
